import { useMemo } from 'react';
import { LineChart, LineChartProps } from '@mui/x-charts/LineChart';
import { COLORS } from '../../../../../utils/colors';
import { SupplierCreditApplicationMonthSummary } from '@dill/dill-shared';

interface ApplicationsTrendChartProps {
  selectedYear: string;
  monthlyData: SupplierCreditApplicationMonthSummary[];
}

const getResponsiveConfig = (): Partial<LineChartProps> => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return {
    height: isMobile ? 350 : 400,
    margin: {
      left: isMobile ? 60 : 80,
      right: isMobile ? 15 : 20,
      top: 20,
      bottom: isMobile ? 80 : 60,
    },
  };
};

function ApplicationsTrendChart({ selectedYear, monthlyData }: ApplicationsTrendChartProps) {
  const chartData = useMemo(() => {
    return monthlyData.length > 0
      ? [...monthlyData]
          .sort((a, b) => parseInt(a.month) - parseInt(b.month))
          .map((data) => ({
            month: data.monthName.substring(0, 3), // Convert "January" to "Jan"
            approvedApplications: data.approvedApplications,
            newApplications: data.totalApplications,
            rejectedApplications: data.rejectedApplications,
          }))
      : [];
  }, [monthlyData]);

  return (
    <div className="w-full">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
            Applications Trend
          </h3>
          <p className="text-sm text-gray-500">
            Monthly approved, new, and rejected applications for {selectedYear}
          </p>
        </div>
      </div>

      <LineChart
        dataset={chartData}
        xAxis={[
          {
            dataKey: 'month',
            scaleType: 'point',
            tickLabelStyle: { fontSize: 12, fill: COLORS.TEXT_SECONDARY },
          },
        ]}
        series={[
          {
            dataKey: 'approvedApplications',
            label: 'Approved Applications',
            color: COLORS.SUCCESS_500,
            curve: 'linear',
            valueFormatter: (value: number | null) => {
              return `${value || 0}`;
            },
          },
          {
            dataKey: 'newApplications',
            label: 'New Applications',
            color: COLORS.PRIMARY_500,
            curve: 'linear',
            valueFormatter: (value: number | null) => {
              return `${value || 0}`;
            },
          },
          {
            dataKey: 'rejectedApplications',
            label: 'Rejected Applications',
            color: COLORS.ERROR_500,
            curve: 'linear',
            valueFormatter: (value: number | null) => {
              return `${value || 0}`;
            },
          },
        ]}
        yAxis={[
          {
            tickLabelStyle: { fontSize: 11, fill: COLORS.TEXT_SECONDARY },
            min: 0,
          },
        ]}
        grid={{ horizontal: true, vertical: false }}
        {...getResponsiveConfig()}
      />
    </div>
  );
}

export default ApplicationsTrendChart;
